import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import OrderController from '@/services/OrderController';
import useStore from '@/store/store';
import useInterval from '@/utils/useInterval';
import { injectTimes } from '@/utils/inject-times';
import { getOrderPreparationStatus } from '@/utils/get-order-preparation-status';
import { sortActiveOrdersNoCarrying } from '@/utils/sort-active-orders';

const MIN_TIME_BEFORE_FOCUS_CALL = 15_000;

const showFetchError = res => {
	if (res?.error) {
		toast.error(
			`Κάποιο πρόβλημα προέκυψε${typeof res.error === 'string' || res?.code ? ':' : ''}${
				typeof res.error === 'string' ? ` ${res.error}` : ''
			}${res?.code ? ` (${res.code})` : ''}`,
		);
	} else {
		console.log('ERROR  -- ', res);
	}
};

export const useOrdersData = ({ user, isMasterOrAdmin }) => {
	const { activeOrders, setActiveOrders, draftOrders, setDraftOrders } = useStore();

	const activeOrdersRef = useRef([]);
	const draftOrdersRef = useRef([]);
	const lastTimeFocusCall = useRef(0);

	useEffect(() => {
		activeOrdersRef.current = activeOrders;
	}, [activeOrders]);

	useEffect(() => {
		draftOrdersRef.current = draftOrders;
	}, [draftOrders]);

	// The socket handlers read these refs and then write back through the setters.
	// The effects above only sync after React commits, so two events landing before
	// a re-render would both read the same stale value and the second would drop the
	// first. Writing the ref alongside the setter keeps it correct within a tick.
	const setActiveOrdersSynced = value => {
		activeOrdersRef.current = value;
		setActiveOrders(value);
	};

	// The backend returns drafts in no explicit order and the socket appends new
	// ones, so the board is ordered here: oldest first, the order a dispatcher
	// should work through them in.
	const sortDraftOrders = orders =>
		Array.isArray(orders)
			? [...orders].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
			: orders;

	const setDraftOrdersSynced = value => {
		const sorted = sortDraftOrders(value);
		draftOrdersRef.current = sorted;
		setDraftOrders(sorted);
	};

	const sortOrders = (orders, newOrder = undefined) => {
		const activeOrders = (newOrder ? [newOrder, ...orders] : [...orders]).map(o => ({
			...o,
			preparationStatus: getOrderPreparationStatus(o)?.preparationStatus,
		}));
		if (!activeOrders?.length) {
			return [];
		}

		return sortActiveOrdersNoCarrying(
			activeOrders,
			user?.cityLocation?.continuousOrderDisplayTime,
			user?.cityLocation?.separateSortCarrying,
		);
	};

	const updateTimes = () => {
		if (!activeOrders) {
			return;
		}
		setActiveOrdersSynced(sortOrders(activeOrders.map(o => injectTimes({ ...o }))));
	};
	useInterval(updateTimes, isMasterOrAdmin ? 6_000 : 9_000);

	const fetchActiveOrders = async () => {
		if (user?.role === 'master' && !user?.cityLocation?.id) {
			return;
		}

		const res = await OrderController.getActive(user?.cityLocation?.id);

		if (res?.activeOrders) {
			if (res?.activeOrders?.length === 0) {
				return setActiveOrdersSynced([]);
			}
			const activeOrders = res?.activeOrders.map(o => injectTimes({ ...o }));

			setActiveOrdersSynced(sortOrders(activeOrders));
		} else {
			showFetchError(res);
		}
	};
	useInterval(fetchActiveOrders, 3 * 60_000);

	const fetchDraftOrders = async () => {
		if (user?.role === 'master' && !user?.cityLocation?.id) {
			return;
		}

		const res = await OrderController.getDraft(user?.cityLocation?.id);

		if (res?.draftOrders) {
			if (res?.draftOrders?.length === 0) {
				return setDraftOrdersSynced([]);
			}
			setDraftOrdersSynced(res.draftOrders);
		} else {
			showFetchError(res);
		}
	};
	useInterval(fetchDraftOrders, 3 * 60_000);

	useEffect(() => {
		if (user?.cityLocation) {
			fetchActiveOrders();
			fetchDraftOrders();
		}
	}, [user?.cityLocation]);

	useEffect(() => {
		const onFocusPage = () => {
			const now = new Date().getTime();
			if (now - lastTimeFocusCall.current > MIN_TIME_BEFORE_FOCUS_CALL) {
				lastTimeFocusCall.current = now;

				fetchActiveOrders();
				fetchDraftOrders();
			}
		};

		window.addEventListener('focus', onFocusPage);
		return () => {
			window.removeEventListener('focus', onFocusPage);
		};
	}, []);

	return {
		activeOrders,
		setActiveOrders: setActiveOrdersSynced,
		draftOrders,
		setDraftOrders: setDraftOrdersSynced,
		activeOrdersRef,
		draftOrdersRef,
		sortOrders,
		fetchActiveOrders,
		fetchDraftOrders,
	};
};
